import {
  Button,
  Checkbox,
  Form,
  FormProps,
  Input,
  InputNumber,
  Modal,
  Select,
  Spin,
} from "antd";
import { Save, X } from "lucide-react";
import { CustomizeRequiredMark } from "~/components/common/custom-required-mark";
import { capitalize } from "~/utils";
import { SuppliesType } from "~/utils/constants";
import { categories, defaultCategories } from "./get-category";
import { useGetLocationsInfinite } from "~/services/common";
import {
  CreateSupplyParams,
  SUPPLY_KEYS,
  useCreateSupply,
  useGetSupply,
  useUpdateSupply,
} from "~/services/supply";
import { useNotification } from "~/hooks";
import { useSupplyStore } from "./supply.store";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useGetVendors } from "~/services/vendor";
import { InfiniteScrollSelect } from "~/components/common";

export const ModalSupply = () => {
  const qc = useQueryClient();
  const [form] = Form.useForm<CreateSupplyParams>();
  const { filters, formState, closeSupplyModal } = useSupplyStore();

  const infiniteLocations = useGetLocationsInfinite();
  const { data: vendors } = useGetVendors();

  const { data: supply, isLoading: isLoadingSupply } = useGetSupply(
    formState.supplyId
  );

  useEffect(() => {
    if (!supply) return;
    form.setFieldsValue({
      ...supply,
      suppliesType: supply.suppliesType,
      locationId: supply.location?.id,
      category: supply.category,
      isBillable: supply.isBillable ?? false,
      billableRate: supply.billableRate,
    });
  }, [supply, form]);

  const activeTab = Form.useWatch("suppliesType", form) as SuppliesType;

  const { success, error } = useNotification();

  const createMutation = useCreateSupply();
  const updateMutation = useUpdateSupply();

  const handleClose = () => {
    form.resetFields();
  };

  const handleSubmit: FormProps["onFinish"] = async (values) => {
    const { suppliesType, ...rest } = values;

    const formattedValues = {
      ...rest,
      suppliesType,
      cost: parseFloat(rest.cost ?? rest.price),
      price: parseFloat(rest.price ?? rest.cost),
      isBillable: rest.isBillable ?? false,
      billableRate: rest.billableRate ? parseFloat(rest.billableRate) : null,
    };

    const mutation = formState.supplyId ? updateMutation : createMutation;
    const payload = formState.supplyId
      ? { id: formState.supplyId, ...formattedValues }
      : formattedValues;

    mutation.mutate(payload, {
      onSuccess: () => {
        success({
          message: "Item added successfully",
          description: "The item has been added to the inventory.",
        });
        closeSupplyModal();
        qc.invalidateQueries({
          queryKey: SUPPLY_KEYS.GET_SUPPLIES(filters),
        });
      },
      onError: (err) => {
        error({
          message: "Error adding item",
          description: err?.message || "An error occurred.",
        });
      },
    });
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal
      open={formState.open}
      onCancel={() => {
        closeSupplyModal();
        handleClose();
      }}
      width={"80%"}
      destroyOnHidden
      footer={null}
    >
      <Spin spinning={isLoadingSupply} size="large">
        <Form
          form={form}
          size="large"
          layout="vertical"
          requiredMark={CustomizeRequiredMark}
          initialValues={{
            suppliesType: SuppliesType.PACKAGING_MATERIALS,
          }}
          onFinish={handleSubmit}
        >
          <h1 className="text-2xl font-bold mb-4">
            Internal Supply Management System
          </h1>

          <div className="mb-6">
            <Form.Item name="suppliesType">
              <SelectSupplyType />
            </Form.Item>
          </div>

          <div className="bg-white rounded-lg w-full">
            <div className="pb-4 mb-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-2xl font-semibold">
                {activeTab === SuppliesType.PACKAGING_MATERIALS
                  ? "Add New Raw Material"
                  : activeTab === SuppliesType.TOOLS_KITS_AND_HARDWARE
                  ? "Add New Tools, Kits and Hardware"
                  : activeTab === SuppliesType.INTERNAL_SUPPLIES_AND_FACILITIES
                  ? "Add New Internal Supplies and Facilities"
                  : activeTab === SuppliesType.PPE
                  ? "Add New PPE"
                  : activeTab === SuppliesType.REFURBISHED_CRATES
                  ? "Add New Refurbished Crate"
                  : "Add New Equipment"}
              </h2>
              <button
                onClick={handleClose}
                type="button"
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-2 gap-8">
                {/* LEFT COLUMN */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Basic Information</h3>

                  {/* Name */}
                  <Form.Item
                    name="name"
                    label="Item Name"
                    rules={[{ required: true, message: "Please input the item name!" }]}
                  >
                    <Input placeholder="Enter item name" size="large" />
                  </Form.Item>

                  {/* Type/Category */}
                  <Form.Item
                    name="category"
                    label="Type/Category"
                    rules={[{ required: true, message: "Please select a type/category!" }]}
                  >
                    <Select
                      placeholder="Select Type"
                      size="large"
                      options={(
                        categories[activeTab as keyof typeof categories] ?? defaultCategories
                      )?.map((category) => ({
                        label: capitalize(category.name),
                        value: category.id,
                      }))}
                    />
                  </Form.Item>

                  {/* Location */}
                  <Form.Item name="locationId" label="Storage Location">
                    <InfiniteScrollSelect
                      queryResult={infiniteLocations}
                      placeholder="Select Location"
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.id}
                    />
                  </Form.Item>

                  {/* Vendor */}
                  <Form.Item name="vendorId" label="Vendor/Supplier">
                    <Select
                      placeholder="Select Vendor"
                      size="large"
                      options={vendors?.items.map((vendor) => ({
                        label: vendor.name,
                        value: vendor.id,
                      }))}
                    />
                  </Form.Item>

                  {/* Brand (if applicable) */}
                  {(activeTab === SuppliesType.PACKAGING_MATERIALS ||
                    activeTab === SuppliesType.INTERNAL_SUPPLIES_AND_FACILITIES ||
                    activeTab === SuppliesType.REFURBISHED_CRATES) && (
                    <Form.Item name="brand" label="Brand">
                      <Input placeholder="Brand name" size="large" />
                    </Form.Item>
                  )}

                  {/* Division for Internal Supplies */}
                  {activeTab === SuppliesType.INTERNAL_SUPPLIES_AND_FACILITIES && (
                    <Form.Item
                      name="division"
                      label="Division"
                      rules={[{ required: true, message: "Please select a division!" }]}
                    >
                      <Select
                        placeholder="Select Division"
                        size="large"
                        options={[
                          { label: "Workshop", value: "workshop" },
                          { label: "Transport", value: "transport" },
                          { label: "Office", value: "office" },
                          { label: "Storage", value: "storage" },
                        ]}
                      />
                    </Form.Item>
                  )}
                </div>

                {/* RIGHT COLUMN */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Pricing & Inventory</h3>

                  {/* Cost */}
                  <Form.Item
                    name="cost"
                    label="Cost (per unit)"
                    rules={[
                      {
                        required:
                          activeTab === SuppliesType.TOOLS_KITS_AND_HARDWARE ||
                          activeTab === SuppliesType.EQUIPMENT,
                        message: "Please input the cost!",
                      },
                    ]}
                  >
                    <InputNumber
                      placeholder="0.00"
                      prefix="$"
                      step="0.01"
                      size="large"
                      className="w-full"
                    />
                  </Form.Item>

                  {/* Unit */}
                  <Form.Item
                    name="unit"
                    label="Unit of Measure"
                    rules={[{ required: true, message: "Please input the unit!" }]}
                  >
                    <Input placeholder="e.g., box, roll, each" size="large" />
                  </Form.Item>

                  {/* Billable Checkbox */}
                  <Form.Item
                    name="isBillable"
                    valuePropName="checked"
                    initialValue={false}
                  >
                    <Checkbox>
                      <span className="font-medium">Billable?</span>
                      <br />
                      <span className="text-xs text-gray-500">
                        Check if this item can be billed to clients
                      </span>
                    </Checkbox>
                  </Form.Item>

                  {/* Billable Rate - shown only when billable is checked */}
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                      prevValues.isBillable !== currentValues.isBillable
                    }
                  >
                    {({ getFieldValue }) =>
                      getFieldValue("isBillable") ? (
                        <Form.Item name="billableRate" label="Price (if billable)">
                          <InputNumber
                            placeholder="0.00"
                            prefix="$"
                            step="0.01"
                            size="large"
                            className="w-full"
                          />
                        </Form.Item>
                      ) : null
                    }
                  </Form.Item>

                  {/* Track Stock Checkbox */}
                  {activeTab !== SuppliesType.INTERNAL_SUPPLIES_AND_FACILITIES && (
                    <Form.Item
                      name="trackStock"
                      valuePropName="checked"
                      initialValue={true}
                    >
                      <Checkbox>
                        <span className="font-medium">Track inventory levels</span>
                        <br />
                        <span className="text-xs text-gray-500">
                          Alert when stock falls below minimum
                        </span>
                      </Checkbox>
                    </Form.Item>
                  )}

                  {/* Current & Minimum Stock */}
                  {activeTab !== SuppliesType.INTERNAL_SUPPLIES_AND_FACILITIES && (
                    <div className="grid grid-cols-2 gap-4">
                      <Form.Item
                        name="currentStock"
                        label="Current Stock"
                        dependencies={["trackStock"]}
                        rules={[
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              const trackStock = getFieldValue("trackStock");
                              if (trackStock !== false && !value && value !== 0) {
                                return Promise.reject(
                                  new Error("Required when tracking stock!")
                                );
                              }
                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        <InputNumber
                          placeholder="0"
                          step="1"
                          size="large"
                          className="w-full"
                        />
                      </Form.Item>

                      <Form.Item
                        name="minimumStock"
                        label="Minimum Stock"
                        dependencies={["trackStock"]}
                        rules={[
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              const trackStock = getFieldValue("trackStock");
                              if (trackStock !== false && !value && value !== 0) {
                                return Promise.reject(
                                  new Error("Required when tracking stock!")
                                );
                              }
                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        <InputNumber
                          placeholder="0"
                          step="1"
                          size="large"
                          className="w-full"
                        />
                      </Form.Item>
                    </div>
                  )}
                </div>
              </div>

              {/* Dimensions Section (for Packaging Materials and Refurbished Crates) - Full Width */}
              {(activeTab === SuppliesType.PACKAGING_MATERIALS ||
                activeTab === SuppliesType.REFURBISHED_CRATES) && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">
                    {activeTab === SuppliesType.REFURBISHED_CRATES
                      ? "Outside Dimensions"
                      : "Dimensions (for coverage calculation)"}
                  </h3>
                  <div className="grid grid-cols-4 gap-4">
                    <Form.Item name="length" label="Length (in)">
                      <InputNumber
                        placeholder="0.00"
                        step="0.01"
                        size="large"
                        className="w-full"
                      />
                    </Form.Item>

                    <Form.Item name="width" label="Width (in)">
                      <InputNumber
                        placeholder="0.00"
                        step="0.01"
                        size="large"
                        className="w-full"
                      />
                    </Form.Item>

                    <Form.Item name="height" label="Height (in)">
                      <InputNumber
                        placeholder="0.00"
                        step="0.01"
                        size="large"
                        className="w-full"
                      />
                    </Form.Item>

                    <Form.Item name="weight" label="Weight (lbs)">
                      <InputNumber
                        placeholder="0.00"
                        step="0.01"
                        size="large"
                        className="w-full"
                      />
                    </Form.Item>
                  </div>

                  {/* Inside Cavity Dimensions (for Refurbished Crates only) */}
                  {activeTab === SuppliesType.REFURBISHED_CRATES && (
                    <>
                      <h3 className="text-lg font-semibold text-gray-700 mb-4 mt-6">
                        Inside Cavity Dimensions
                      </h3>
                      <div className="grid grid-cols-3 gap-4">
                        <Form.Item name="insideLength" label="Inside Length (in)">
                          <InputNumber
                            placeholder="0.00"
                            step="0.01"
                            size="large"
                            className="w-full"
                          />
                        </Form.Item>

                        <Form.Item name="insideWidth" label="Inside Width (in)">
                          <InputNumber
                            placeholder="0.00"
                            step="0.01"
                            size="large"
                            className="w-full"
                          />
                        </Form.Item>

                        <Form.Item name="insideHeight" label="Inside Height (in)">
                          <InputNumber
                            placeholder="0.00"
                            step="0.01"
                            size="large"
                            className="w-full"
                          />
                        </Form.Item>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Description (for Tools/Equipment) - Full Width */}
              {(activeTab === SuppliesType.TOOLS_KITS_AND_HARDWARE ||
                activeTab === SuppliesType.EQUIPMENT) && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <Form.Item name="description" label="Description">
                    <Input.TextArea
                      rows={3}
                      placeholder="Provide a description..."
                      className="text-base"
                    />
                  </Form.Item>
                </div>
              )}
            </div>

            <div className="p-4 border-t bg-gray-50 flex justify-end space-x-3">
              <Button
                htmlType="button"
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                Cancel
              </Button>
              <Button
                loading={isLoading}
                htmlType="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
              >
                <Save size={16} />
                <span>Save Item</span>
              </Button>
            </div>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
};

const SelectSupplyType = ({
  value,
  onChange,
}: {
  value?: SuppliesType;
  onChange?: (val: SuppliesType) => void;
}) => {
  const getDisplayName = (type: SuppliesType) => {
    switch (type) {
      case SuppliesType.PACKAGING_MATERIALS:
        return "Raw Materials";
      case SuppliesType.TOOLS_KITS_AND_HARDWARE:
        return "Tools, Kits and Hardware";
      case SuppliesType.INTERNAL_SUPPLIES_AND_FACILITIES:
        return "Internal Supplies and Facilities";
      case SuppliesType.REFURBISHED_CRATES:
        return "Refurbished Crates";
      default:
        return capitalize(type);
    }
  };

  return (
    <div className="bg-white rounded-lg overflow-hidden">
      <div className="flex gap-2">
        {Object.values(SuppliesType).map((type) => (
          <button
            key={type}
            type="button"
            className={`flex-1 py-3 px-4 text-center font-medium rounded-lg transition-all ${
              value === type
                ? "bg-blue-600 text-white shadow-sm"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
            onClick={() => onChange?.(type)}
          >
            {getDisplayName(type)}
          </button>
        ))}
      </div>
    </div>
  );
};
